import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { getInitialSiteNameDisplay, saveSiteName, parseSiteName, DEFAULT_SITE_NAME } from '../utils/siteName';
import { 
  Home, 
  Video, 
  FileText, 
  Trophy, 
  Crown, 
  LogOut, 
  ChevronDown,
  ArrowRight,
  Brain
} from 'lucide-react';
import './Navbar.css';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [siteName, setSiteName] = useState(getInitialSiteNameDisplay);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  // 加载网站名称设置
  useEffect(() => {
    const loadSiteName = async () => {
      try {
        const response = await api.get('/settings/siteName');
        if (response.data.success && response.data.data) {
          setSiteName(saveSiteName(response.data.data));
        } else {
          setSiteName(parseSiteName(DEFAULT_SITE_NAME));
        }
      } catch (error) {
        console.error('Failed to load site name:', error);
      }
    };
    loadSiteName();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    const handleClickOutside = () => setShowUserMenu(false);
    if (showUserMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showUserMenu]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/videos', label: 'Videos', icon: Video },
    { path: '/documents', label: 'Documents', icon: FileText },
    { path: '/quiz', label: 'Quiz', icon: Brain },
    { path: '/leaderboard', label: 'Leaderboard', icon: Trophy },
    { path: '/vip', label: 'VIP Center', icon: Crown },
  ];

  return (
    <nav className={`navbar ${isScrolled ? 'navbar--scrolled' : ''}`}>
      <div className="navbar__container">
        {/* Logo */}
        <Link to="/" className="navbar__logo">
          <div className="navbar__logo-icon">
            <img
              src="/logo.png"
              alt={siteName.full || 'Site logo'}
              className="navbar__logo-img"
            />
          </div>
          <div className="navbar__logo-text">
            <span className="navbar__logo-title">{siteName.title}</span>
            {siteName.subtitle && <span className="navbar__logo-subtitle">{siteName.subtitle}</span>}
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="navbar__links">
          {navLinks.map((link) => {
            const IconComponent = link.icon;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`navbar__link ${location.pathname === link.path ? 'navbar__link--active' : ''}`}
              >
                <IconComponent size={18} className="navbar__link-icon" />
                <span className="navbar__link-label">{link.label}</span>
              </Link>
            );
          })}
        </div>

        {/* Actions */}
        <div className="navbar__actions">
          {isAuthenticated ? (
            <div
              className={`navbar__user${user?.role === 'vip' ? ' navbar__user--vip' : ''}`}
              onClick={(e) => { e.stopPropagation(); setShowUserMenu(!showUserMenu); }}
            >
              <div className="navbar__user-avatar">
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <span className="navbar__user-name">{user?.username}</span>
              <ChevronDown size={16} className={`navbar__user-arrow ${showUserMenu ? 'navbar__user-arrow--open' : ''}`} />
              
              {showUserMenu && (
                <div className="navbar__user-menu">
                  <div className="navbar__user-menu-header">
                    <strong>{user?.username}</strong>
                    <span>{user?.email}</span>
                    {user?.role === 'vip' && <span className="navbar__user-badge">VIP</span>}
                  </div>
                  <div className="navbar__user-menu-divider"></div>
                  <Link to="/vip" className="navbar__user-menu-item" onClick={() => setShowUserMenu(false)}>
                    <Crown size={16} /> VIP Center
                  </Link>
                  <button className="navbar__user-menu-item navbar__user-menu-item--danger" onClick={handleLogout}>
                    <LogOut size={16} /> Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" className="navbar__btn navbar__btn--ghost">
                <span>Sign In</span>
              </Link>
              <Link to="/register" className="navbar__btn navbar__btn--primary">
                <span>Get Started</span>
                <ArrowRight size={18} />
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu Toggle */}
        <button 
          className={`navbar__mobile-toggle ${isMobileMenuOpen ? 'navbar__mobile-toggle--open' : ''}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`navbar__mobile-menu ${isMobileMenuOpen ? 'navbar__mobile-menu--open' : ''}`}>
        {navLinks.map((link) => {
          const IconComponent = link.icon;
          return (
            <Link
              key={link.path}
              to={link.path}
              className={`navbar__mobile-link ${location.pathname === link.path ? 'navbar__mobile-link--active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <IconComponent size={20} className="navbar__mobile-link-icon" />
              <span>{link.label}</span>
            </Link>
          );
        })}
        <div className="navbar__mobile-actions">
          {isAuthenticated ? (
            <>
              <div className="navbar__mobile-user">
                <div className="navbar__user-avatar">{user?.username?.charAt(0).toUpperCase() || 'U'}</div>
                <div>
                  <strong>{user?.username}</strong>
                  <span>{user?.email}</span>
                </div>
              </div>
              <button className="navbar__btn navbar__btn--ghost navbar__btn--full" onClick={handleLogout}>
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="navbar__btn navbar__btn--ghost navbar__btn--full" onClick={() => setIsMobileMenuOpen(false)}>
                Sign In
              </Link>
              <Link to="/register" className="navbar__btn navbar__btn--primary navbar__btn--full" onClick={() => setIsMobileMenuOpen(false)}>
                Get Started
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
